import { Box, Stack, Text } from '@chakra-ui/react'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { MONTHS } from '@/utils/constants'
import countries from '@/utils/countries'

import OnboardingInputPronounsRadio from '@/components/Onboarding/OnboardingInputPronounsRadio'
import type { FormInputType, FormOnValidate, FormValues } from '@/components/UI/Form/types'
import Tippy from '@/components/UI/Tippy'

import { BIRTH_YEARS, PHONE_NUMBER_INPUT_NAME, ZIPCODE_INPUT_NAME } from './constants'

const ZIPCODE_REGEX = /^\d{5}(-\d{4})?$/

function ZipcodeReasonTooltip(): React.ReactElement {
  return (
    <Tippy
      content={
        <Box alignItems="center">
          <Text color="fg.inverted">
            We collect ZIP Codes for users in the U.S. for demographic research. By providing your
            ZIP Code, you&apos;ll help us take steps to broaden access to education.
          </Text>
          <br />
          <Text color="fg.inverted">We will keep your ZIP code private.</Text>
        </Box>
      }
    >
      <Box>
        <FontAwesomeIcon icon={faInfoCircle} color={chakraTokenToVar('fg.subtle')} />
      </Box>
    </Tippy>
  )
}

export const PRONOUN_OPTIONS = [
  { value: 'She/Her', label: 'She / Her' },
  { value: 'He/Him', label: 'He / Him' },
  { value: 'They/Them', label: 'They / Them' },
  { value: 'She/They', label: 'She / They' },
  { value: 'He/They', label: 'He / They' },
]

const PRIORITIZED_COUNTRIES = ['US']

export function getAboutYouFormInputs({
  userFirstName,
  userLastName,
  userPronouns,
  userCountry,
  userZipcode,
  userBirthday,
  isHomeworkHelpInterest,
}: {
  userFirstName: string | null
  userLastName: string | null
  userPronouns: string | null
  userCountry: string | null
  userZipcode: string | null
  userBirthday: string | null
  isHomeworkHelpInterest: boolean
}): FormInputType[] {
  const prioritizedCountries = countries.filter(country =>
    PRIORITIZED_COUNTRIES.includes(country.code)
  )

  const otherCountries = countries.filter(country => !PRIORITIZED_COUNTRIES.includes(country.code))
  const initialBirthday =
    userBirthday == null
      ? null
      : DateTime.fromISO(userBirthday).setZone('local', { keepLocalTime: true }).endOf('month')
  // The initial date of birth we get is from Auth0, and is before we get any timezone information. So we keep it in UTC
  // In here, we want to keep the user selection of date/year but convert it to local timezone

  return [
    {
      label: 'First name',
      sublabel: 'This will be shown publicly.',
      type: 'text',
      name: 'first_name',
      placeholder: 'Your first name',
      required: true,
      initialValue: userFirstName,
      size: 3,
    },
    {
      label: 'Last name',
      sublabel: 'Only the first letter will be displayed publicly.',
      type: 'text',
      name: 'last_name',
      placeholder: 'Your last name',
      required: true,
      initialValue: userLastName,
      size: 3,
    },
    {
      label: 'Country',
      type: 'select',
      name: 'country',
      sublabel: (
        <Text fontSize="14px" color="fg.muted" padding="0px" margin="0px">
          Your primary location
        </Text>
      ), // This sublabel is only added to vertically align country with the ZIP code
      placeholder: 'Select a country',
      required: true,
      options: prioritizedCountries.concat(otherCountries).map(item => ({
        value: item.code,
        label: item.name,
      })),
      initialValue: userCountry,
      size: 3,
    },
    {
      label: 'ZIP Code',
      sublabel: (
        <Stack direction="row" spacing={2} align="center">
          <Text fontWeight={400} fontSize="14px" color="fg.muted">
            Why do we collect this?
          </Text>
          <ZipcodeReasonTooltip />
        </Stack>
      ),
      dependencies: {
        keys: ['country'],
        callback(values) {
          return values.country === 'US'
        },
      },
      type: 'text',
      name: ZIPCODE_INPUT_NAME,
      placeholder: 'e.g. 12345',
      required: true,
      initialValue: userZipcode,
      size: 3,
    },
    {
      label: 'What month were you born?',
      type: 'select',
      name: 'month_of_birth',
      required: true,
      options: MONTHS.map((item, index) => ({ label: item, value: `${index + 1}` })),
      initialValue: initialBirthday == null ? null : `${initialBirthday.month}`,
      dependencies: {
        keys: [],
        callback() {
          // Only show if no birthday was provided during signup (ie Google Login)
          return userBirthday == null
        },
      },
      size: 3,
    },
    {
      label: 'What year were you born?',
      type: 'select',
      name: 'year_of_birth',
      required: true,
      options: BIRTH_YEARS.map(item => ({ label: `${item}`, value: `${item}` })),
      initialValue: initialBirthday == null ? null : `${initialBirthday.year}`,
      dependencies: {
        keys: [],
        callback() {
          // Only show if no birthday was provided during signup (ie Google Login)
          return userBirthday == null
        },
      },
      size: 3,
    },
    ...(isHomeworkHelpInterest
      ? ([
          {
            label: 'Phone number',
            type: 'phone',
            name: PHONE_NUMBER_INPUT_NAME,
            placeholder: 'Your phone number',
            required: true,
            size: 6,
          },
        ] as FormInputType[])
      : []),
    {
      label: 'Your pronouns (optional)',
      sublabel: 'Help others know how to refer to you in Zoom sessions!',
      type: 'radio',
      name: 'pronouns',
      options: PRONOUN_OPTIONS,
      required: false,
      initialValue: userPronouns,
      Component: OnboardingInputPronounsRadio,
      size: 6,
    },
  ]
}

export function getAboutYouOnValidate(): FormOnValidate {
  return (values: FormValues) => {
    if (values.country !== 'US') {
      return null
    }
    const isValidZipcode = ZIPCODE_REGEX.test(values.zipcode as string)
    if (!isValidZipcode) {
      return {
        message: 'Please provide a valid U.S. ZIP code!',
        errors: {
          zipcode: 'Invalid zipcode',
        },
      }
    }

    return null
  }
}
